import { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { api, apiError } from "../lib/api";
import { openPrintWindow, esc } from "../lib/print";
import { PageHeader, Card, Btn, Empty, Table, Td, Badge } from "../components/UI";
import { FLabel, FInput, FSelect } from "./engagement/FormControls";
import { useClients, ClientSelect } from "./clinical/shared";
import { usePagination, PaginationBar } from "../components/Pagination";
import { DollarSign, Zap, FileOutput, Printer, Download, ShieldCheck, CheckCircle2, XCircle, Receipt, RefreshCw, MessageSquarePlus, AlertOctagon } from "lucide-react";
import { toast } from "sonner";

const fmt = (n) => `$${Number(n || 0).toFixed(2)}`;

const CARC = [
  ["CO-16", "Claim lacks information / billing error"],
  ["CO-18", "Duplicate claim"],
  ["CO-29", "Timely filing limit expired"],
  ["CO-45", "Charge exceeds fee schedule"],
  ["CO-50", "Not medically necessary"],
  ["CO-96", "Non-covered service"],
  ["CO-97", "Bundled into another service"],
  ["CO-109", "Wrong payer / not covered by this payer"],
  ["CO-197", "Authorization missing or expired"],
  ["PR-27", "Coverage terminated"],
];

function EncountersTab({ onClaimCreated }) {
  const [rows, setRows] = useState([]);
  const [sel, setSel] = useState([]);
  const [busy, setBusy] = useState(false);
  const [statusF, setStatusF] = useState("");
  const load = () => api.get(`/billing/encounters${statusF ? `?status=${statusF}` : ""}`).then((r) => { setRows(r.data); setSel([]); }).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, [statusF]); // eslint-disable-line react-hooks/exhaustive-deps
  const pg = usePagination(rows);
  const capture = async () => {
    setBusy(true);
    try {
      const r = await api.post("/billing/capture");
      toast.success(`${r.data.created} encounter(s) captured from signed docs & group attendance - authorization units burned`);
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const createClaim = async () => {
    try {
      const r = await api.post("/billing/claims", { encounter_ids: sel });
      toast.success(`Claim ${r.data.claim_number} created - ${fmt(r.data.total)}`);
      load(); onClaimCreated();
    } catch (e) { toast.error(apiError(e)); }
  };
  const voidEnc = async (id) => {
    try { await api.delete(`/billing/encounters/${id}`); toast.success("Encounter voided - auth units restored"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const unbilledSel = sel.length > 0;
  const totalSel = rows.filter((r) => sel.includes(r.id)).reduce((a, r) => a + r.charge_amount, 0);
  return (
    <div data-testid="rc-encounters">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Btn onClick={capture} disabled={busy} data-testid="rc-capture-btn"><Zap size={14} /> {busy ? "Capturing…" : "Capture Encounters"}</Btn>
        <FSelect data-testid="rc-enc-status-filter" className="w-40 h-10" value={statusF} onChange={(e) => setStatusF(e.target.value)} placeholder="All statuses">
          <option value="unbilled">Unbilled</option><option value="billed">Billed</option><option value="paid">Paid</option><option value="denied">Denied</option><option value="void">Void</option>
        </FSelect>
        {unbilledSel && <Btn variant="outline" onClick={createClaim} data-testid="rc-create-claim-btn"><FileOutput size={14} /> Create Claim ({sel.length} · {fmt(totalSel)})</Btn>}
      </div>
      <Card className="overflow-hidden">
        {rows.length === 0 ? <Empty text="No encounters yet - sign a billable note or click Capture Encounters." /> : (
          <Table headers={["", "Date", "Client", "CPT", "Description", "Units", "Charge", "Auth", "Status", ""]}>
            {pg.pageRows.map((e) => (
              <tr key={e.id} className="border-t border-[#F1EEE7] hover:bg-[#FDFCF8]" data-testid={`rc-enc-row-${e.id}`}>
                <Td><input type="checkbox" data-testid={`rc-enc-select-${e.id}`} disabled={e.status !== "unbilled"} className="h-4 w-4 accent-[#4E342E]"
                  checked={sel.includes(e.id)} onChange={() => setSel((s) => s.includes(e.id) ? s.filter((x) => x !== e.id) : [...s, e.id])} /></Td>
                <Td>{e.date ? dayjs(e.date).format("MM/DD/YY") : "-"}</Td>
                <Td className="font-medium">{e.client_name}</Td>
                <Td className="font-mono text-xs">{e.cpt}</Td>
                <Td className="max-w-[200px] truncate">{e.description}</Td>
                <Td>{e.units}</Td>
                <Td className="font-semibold">{fmt(e.charge_amount)}</Td>
                <Td>{e.auth_id ? <span title="Authorization units burned" className="text-[#2E7D32]"><CheckCircle2 size={14} /></span> : <span className="text-[#84675B] text-xs">-</span>}</Td>
                <Td><Badge value={e.status} /></Td>
                <Td>{e.status === "unbilled" && <button data-testid={`rc-enc-void-${e.id}`} onClick={() => voidEnc(e.id)} className="text-xs font-semibold text-[#C62828] hover:underline">Void</button>}</Td>
              </tr>
            ))}
          </Table>
        )}
        <PaginationBar {...pg} testId="rc-enc-pagination" />
      </Card>
    </div>
  );
}

function printCMS1500(d) {
  const lines = d.service_lines.map((l) => `<tr><td>${esc(l.date)}</td><td>11</td><td>${esc(l.cpt)}</td><td>${esc(l.description)}</td><td>${l.units}</td><td>$${l.charge.toFixed(2)}</td></tr>`).join("");
  openPrintWindow(`CMS-1500 ${d.claim.claim_number}`, `
    <div class="clinic"><h1>HEALTH INSURANCE CLAIM FORM (CMS-1500)</h1>${esc(d.provider.name)} · NPI ${esc(d.provider.npi || "-")} · Tax ID ${esc(d.provider.tax_id || "-")}</div>
    <h2>Boxes 1 to 13 · Patient & Insured</h2>
    <div class="label">Patient name (Box 2)</div><div class="value">${esc(d.patient.name)}</div>
    <div class="label">DOB / Sex (Box 3)</div><div class="value">${esc(d.patient.dob)} · ${esc(d.patient.gender)}</div>
    <div class="label">Patient address (Box 5)</div><div class="value">${esc(d.patient.address || "-")}</div>
    <div class="label">Insured / Payer (Box 1a, 11)</div><div class="value">${esc(d.insured.payer)} · Member ID ${esc(d.insured.member_id || "-")} · Group ${esc(d.insured.group_number || "-")} (${esc(d.insured.plan_type || "-")})</div>
    <h2>21 · Diagnosis Codes (ICD-10)</h2><div class="value">${d.diagnoses.map(esc).join(", ")}</div>
    <h2>24 · Service Lines</h2>
    <table style="border-collapse:collapse;width:100%;font-size:11px" border="1" cellpadding="4">
      <tr><th>Date of service</th><th>POS</th><th>CPT/HCPCS</th><th>Description</th><th>Units</th><th>Charge</th></tr>${lines}
    </table>
    <h2>28 · Total Charge</h2><div class="value" style="font-size:16px;font-weight:bold">$${d.claim.total.toFixed(2)}</div>
    <div class="label">Billing provider (Box 33)</div><div class="value">${esc(d.provider.name)} · ${esc(d.provider.address)} · Medicaid ID ${esc(d.provider.medicaid_id || "-")}</div>
    <div class="meta">Claim ${esc(d.claim.claim_number)} · Generated ${dayjs().format("MM/DD/YYYY h:mm A")} · SIMULATED FORM - verify payer requirements before submission</div>`);
}

function ClaimsTab({ refreshKey }) {
  const [rows, setRows] = useState([]);
  const [denyRow, setDenyRow] = useState(null);
  const [denyCode, setDenyCode] = useState("CO-197");
  const [denyReason, setDenyReason] = useState("");
  const load = () => api.get("/billing/claims").then((r) => setRows(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, [refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const pg = usePagination(rows);
  const setStatus = async (c, status, extra = {}) => {
    try { await api.put(`/billing/claims/${c.id}/status`, { status, ...extra }); toast.success(`Claim ${c.claim_number} → ${status}`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const confirmDeny = async () => {
    const label = CARC.find(([c]) => c === denyCode)?.[1] || "";
    await setStatus(denyRow, "denied", { denial_code: denyCode, denial_reason: denyReason || label });
    setDenyRow(null); setDenyReason("");
  };
  const print1500 = async (c) => {
    try { printCMS1500((await api.get(`/billing/claims/${c.id}/cms1500`)).data); }
    catch (e) { toast.error(apiError(e)); }
  };
  const dl837 = async (c) => {
    try {
      const r = await api.get(`/billing/claims/${c.id}/export837`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${c.claim_number}.837`; a.click();
      URL.revokeObjectURL(url);
      toast.success("837P file downloaded (simulated)");
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Card className="overflow-hidden" data-testid="rc-claims">
      {rows.length === 0 ? <Empty text="No claims yet - select unbilled encounters and create a claim." /> : (
        <Table headers={["Claim #", "Client", "Payer", "Lines", "Total", "Status", "Submitted", "Actions"]}>
          {pg.pageRows.map((c) => (
            <tr key={c.id} className="border-t border-[#F1EEE7] hover:bg-[#FDFCF8]" data-testid={`rc-claim-row-${c.id}`}>
              <Td className="font-mono text-xs font-semibold">{c.claim_number}</Td>
              <Td>{c.client_name}</Td>
              <Td className="max-w-[160px] truncate">{c.payer}</Td>
              <Td>{c.line_count}</Td>
              <Td className="font-semibold">{fmt(c.total)}</Td>
              <Td><Badge value={c.status} /></Td>
              <Td className="text-xs text-[#7B6157]">{c.submitted_at ? dayjs(c.submitted_at).format("MM/DD/YY") : "-"}</Td>
              <Td>
                <div className="flex items-center gap-1">
                  {c.status === "draft" && <Btn variant="outline" className="px-2.5 py-1 text-[11px]" data-testid={`rc-claim-submit-${c.id}`} onClick={() => setStatus(c, "submitted")}>Submit</Btn>}
                  {c.status === "submitted" && (
                    <>
                      <button data-testid={`rc-claim-paid-${c.id}`} title="Mark paid" onClick={() => setStatus(c, "paid")} className="rounded-lg p-1.5 text-[#2E7D32] hover:bg-[#E8F5E9]"><CheckCircle2 size={14} /></button>
                      <button data-testid={`rc-claim-denied-${c.id}`} title="Mark denied" onClick={() => setDenyRow(c)} className="rounded-lg p-1.5 text-[#C62828] hover:bg-[#FFEBEE]"><XCircle size={14} /></button>
                    </>
                  )}
                  <button data-testid={`rc-claim-print-${c.id}`} title="Print CMS-1500" onClick={() => print1500(c)} className="rounded-lg p-1.5 text-[#6D4C41] hover:bg-[#F5F2EB]"><Printer size={14} /></button>
                  <button data-testid={`rc-claim-837-${c.id}`} title="Download 837P" onClick={() => dl837(c)} className="rounded-lg p-1.5 text-[#6D4C41] hover:bg-[#F5F2EB]"><Download size={14} /></button>
                </div>
              </Td>
            </tr>
          ))}
        </Table>
      )}
      <PaginationBar {...pg} testId="rc-claims-pagination" />
      {denyRow && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/40" data-testid="rc-deny-dialog">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
            <h3 className="font-heading text-base font-bold text-[#3E2723]">Deny Claim {denyRow.claim_number}</h3>
            <p className="mb-4 text-xs text-[#7B6157]">Record the payer's denial reason - it feeds the Denials workqueue & analytics.</p>
            <FLabel>Denial code (CARC)</FLabel>
            <FSelect data-testid="rc-deny-code" value={denyCode} onChange={(e) => setDenyCode(e.target.value)}>
              {CARC.map(([c, l]) => <option key={c} value={c}>{c} - {l}</option>)}
            </FSelect>
            <div className="mt-3"><FLabel>Payer remark / details (optional)</FLabel>
              <FInput data-testid="rc-deny-reason" value={denyReason} onChange={(e) => setDenyReason(e.target.value)} placeholder="e.g., Auth AUTH-77120 expired 06/01" /></div>
            <div className="mt-5 flex justify-end gap-2">
              <Btn variant="secondary" onClick={() => setDenyRow(null)}>Cancel</Btn>
              <Btn data-testid="rc-deny-confirm-btn" onClick={confirmDeny} className="bg-[#C62828] hover:bg-[#B71C1C]"><XCircle size={14} /> Deny Claim</Btn>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}

function DenialsTab() {
  const [data, setData] = useState({ denials: [], resubmitted: [] });
  const [analytics, setAnalytics] = useState(null);
  const [users, setUsers] = useState([]);
  const [noteRow, setNoteRow] = useState(null);
  const [noteText, setNoteText] = useState("");
  const load = () => Promise.all([
    api.get("/billing/denials").then((r) => setData(r.data)),
    api.get("/billing/denials/analytics").then((r) => setAnalytics(r.data)),
  ]).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); api.get("/users").then((r) => setUsers(r.data)).catch(() => {}); }, []);
  const work = async (c, body, msg) => {
    try { await api.put(`/billing/denials/${c.id}`, body); toast.success(msg); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const resubmit = async (c) => {
    try {
      const r = await api.post(`/billing/claims/${c.id}/resubmit`);
      toast.success(`Resubmitted as ${r.data.claim_number}`); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const saveNote = async () => { await work(noteRow, { note: noteText }, "Work note added"); setNoteRow(null); setNoteText(""); };
  const WS_COLORS = { new: "bg-[#FFEBEE] text-[#C62828]", in_progress: "bg-[#FFF8E1] text-[#8D6E00]", corrected: "bg-[#E3F2FD] text-[#1565C0]", resubmitted: "bg-[#EDE7F6] text-[#5E35B1]", written_off: "bg-[#ECEFF1] text-[#546E7A]" };
  return (
    <div data-testid="rc-denials">
      {analytics && (
        <div className="mb-4 grid gap-3 lg:grid-cols-3">
          <Card className="p-4" data-testid="rc-denial-kpis">
            <div className="text-[10px] font-bold uppercase tracking-wide text-[#84675B]">Denial Rate</div>
            <div className={`font-heading text-3xl font-bold ${analytics.denial_rate > 10 ? "text-[#C62828]" : "text-[#2E7D32]"}`}>{analytics.denial_rate}%</div>
            <div className="mt-1 text-xs text-[#7B6157]">{analytics.denied_count} of {analytics.total_claims} claims · <b className="text-[#C62828]">{fmt(analytics.at_risk)} at risk</b> · {fmt(analytics.recovered)} recovered</div>
          </Card>
          <Card className="p-4" data-testid="rc-denial-by-reason">
            <div className="mb-2 text-[10px] font-bold uppercase tracking-wide text-[#84675B]">Top Denial Reasons</div>
            {analytics.by_reason.length === 0 ? <div className="text-xs text-[#84675B]">No denials 🎉</div> :
              analytics.by_reason.slice(0, 4).map((r) => (
                <div key={r.reason} className="mb-1 flex items-center justify-between text-xs">
                  <span className="truncate pr-2 text-[#3E2723]">{r.reason}</span>
                  <span className="shrink-0 font-semibold text-[#C62828]">{r.count} · {fmt(r.amount)}</span>
                </div>))}
          </Card>
          <Card className="p-4" data-testid="rc-denial-by-payer">
            <div className="mb-2 text-[10px] font-bold uppercase tracking-wide text-[#84675B]">Denials by Payer</div>
            {analytics.by_payer.length === 0 ? <div className="text-xs text-[#84675B]">-</div> :
              analytics.by_payer.slice(0, 4).map((p) => (
                <div key={p.payer} className="mb-1 flex items-center justify-between text-xs">
                  <span className="truncate pr-2 text-[#3E2723]">{p.payer}</span>
                  <span className="shrink-0 font-semibold text-[#C62828]">{p.count} · {fmt(p.amount)}</span>
                </div>))}
          </Card>
        </div>
      )}
      <Card className="overflow-hidden">
        {data.denials.length === 0 ? <Empty text="Denial workqueue is empty - no denied claims." /> : (
          <Table headers={["Claim #", "Client", "Payer", "Total", "Denial", "Assigned To", "Work Status", "Actions"]}>
            {data.denials.map((c) => (
              <tr key={c.id} className="border-t border-[#F1EEE7] hover:bg-[#FDFCF8]" data-testid={`rc-denial-row-${c.id}`}>
                <Td className="font-mono text-xs font-semibold">{c.claim_number}{c.resubmitted_as && <div className="text-[10px] text-[#5E35B1]">→ {c.resubmitted_as}</div>}</Td>
                <Td>{c.client_name}</Td>
                <Td className="max-w-[130px] truncate">{c.payer}</Td>
                <Td className="font-semibold text-[#C62828]">{fmt(c.total)}</Td>
                <Td className="max-w-[180px]"><div className="text-xs font-bold text-[#C62828]">{c.denial_code || "UNK"}</div><div className="truncate text-xs text-[#7B6157]" title={c.denial_reason}>{c.denial_reason || "-"}</div></Td>
                <Td>
                  <FSelect data-testid={`rc-denial-assign-${c.id}`} className="h-8 w-36 text-xs" value={c.assigned_to || ""}
                    onChange={(e) => work(c, { assigned_to: e.target.value, work_status: c.work_status === "new" ? "in_progress" : c.work_status }, `Assigned to ${e.target.value || "-"}`)}>
                    <option value="">Unassigned</option>
                    {users.map((u) => <option key={u.id} value={`${u.first_name} ${u.last_name}`}>{u.first_name} {u.last_name}</option>)}
                  </FSelect>
                </Td>
                <Td><span className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${WS_COLORS[c.work_status] || WS_COLORS.new}`}>{(c.work_status || "new").replace("_", " ")}</span>
                  {(c.work_notes || []).length > 0 && <span className="ml-1 text-[10px] font-bold text-[#7B6157]">+{c.work_notes.length}</span>}</Td>
                <Td>
                  <div className="flex items-center gap-1">
                    {c.work_status !== "written_off" && c.work_status !== "resubmitted" && (
                      <>
                        {c.work_status !== "corrected" && <button data-testid={`rc-denial-corrected-${c.id}`} title="Mark corrected" onClick={() => work(c, { work_status: "corrected" }, "Marked corrected")} className="rounded-lg px-2 py-1 text-[11px] font-semibold text-[#1565C0] hover:bg-[#E3F2FD]">Corrected</button>}
                        <Btn variant="outline" className="px-2.5 py-1 text-[11px]" data-testid={`rc-denial-resubmit-${c.id}`} onClick={() => resubmit(c)}><RefreshCw size={11} /> Resubmit</Btn>
                        <button data-testid={`rc-denial-writeoff-${c.id}`} title="Write off" onClick={() => work(c, { work_status: "written_off" }, "Written off")} className="rounded-lg px-2 py-1 text-[11px] font-semibold text-[#546E7A] hover:bg-[#ECEFF1]">W/O</button>
                      </>
                    )}
                    <button data-testid={`rc-denial-note-${c.id}`} title="Add work note" onClick={() => { setNoteRow(c); setNoteText(""); }} className="rounded-lg p-1.5 text-[#6D4C41] hover:bg-[#F5F2EB]"><MessageSquarePlus size={13} /></button>
                  </div>
                </Td>
              </tr>
            ))}
          </Table>
        )}
      </Card>
      {noteRow && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/40" data-testid="rc-denial-note-dialog">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
            <h3 className="mb-1 font-heading text-base font-bold text-[#3E2723]">Work Notes - {noteRow.claim_number}</h3>
            <div className="mb-3 max-h-40 space-y-2 overflow-y-auto">
              {(noteRow.work_notes || []).map((n, i) => (
                <div key={i} className="rounded-lg bg-[#FDFCF8] border border-[#F1EEE7] p-2 text-xs">
                  <span className="font-semibold text-[#7B6157]">{n.author} · {dayjs(n.at).format("MM/DD h:mm A")}</span>
                  <div className="text-[#3E2723]">{n.text}</div>
                </div>))}
            </div>
            <FInput data-testid="rc-denial-note-text" value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder="e.g., Called Amerigroup, corrected member ID…" />
            <div className="mt-4 flex justify-end gap-2">
              <Btn variant="secondary" onClick={() => setNoteRow(null)}>Close</Btn>
              <Btn data-testid="rc-denial-note-save-btn" onClick={saveNote} disabled={!noteText.trim()}>Add Note</Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function FeeScheduleTab() {
  const [rows, setRows] = useState([]);
  const [add, setAdd] = useState({ cpt: "", description: "", rate: "" });
  const load = () => api.get("/billing/fee-schedule").then((r) => setRows(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);
  const saveRate = async (f, rate) => {
    if (String(f.rate) === String(rate)) return;
    try { await api.put(`/billing/fee-schedule/${f.id}`, { rate }); toast.success(`${f.cpt} rate updated`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const create = async () => {
    try { await api.post("/billing/fee-schedule", add); toast.success("Fee added"); setAdd({ cpt: "", description: "", rate: "" }); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div data-testid="rc-fees">
      <Card className="mb-4 p-4">
        <div className="grid gap-3 sm:grid-cols-4 items-end">
          <div><FLabel>CPT / HCPCS</FLabel><FInput data-testid="rc-fee-cpt" value={add.cpt} onChange={(e) => setAdd((s) => ({ ...s, cpt: e.target.value }))} placeholder="H0038" /></div>
          <div><FLabel>Description</FLabel><FInput data-testid="rc-fee-desc" value={add.description} onChange={(e) => setAdd((s) => ({ ...s, description: e.target.value }))} /></div>
          <div><FLabel>Rate ($)</FLabel><FInput data-testid="rc-fee-rate" type="number" value={add.rate} onChange={(e) => setAdd((s) => ({ ...s, rate: e.target.value }))} /></div>
          <Btn onClick={create} data-testid="rc-fee-add-btn">Add Fee</Btn>
        </div>
      </Card>
      <Card className="overflow-hidden">
        <Table headers={["Code", "Description", "Unit", "Rate ($)"]}>
          {rows.map((f) => (
            <tr key={f.id} className="border-t border-[#F1EEE7]" data-testid={`rc-fee-row-${f.cpt}`}>
              <Td className="font-mono text-xs font-bold">{f.cpt}</Td>
              <Td>{f.description}</Td>
              <Td className="text-xs text-[#7B6157]">{f.unit_minutes === 1 ? "per unit" : `${f.unit_minutes} min`}</Td>
              <Td><input data-testid={`rc-fee-rate-${f.cpt}`} type="number" defaultValue={f.rate} onBlur={(e) => saveRate(f, e.target.value)}
                className="w-24 rounded-lg border border-[#E5DFD3] px-2 py-1 text-sm focus:outline-none focus:border-[#8D6E63]" /></Td>
            </tr>
          ))}
        </Table>
      </Card>
    </div>
  );
}

function EligibilityTab() {
  const clients = useClients();
  const [clientId, setClientId] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    if (!clientId) return toast.error("Select a client");
    setBusy(true);
    try { setResult((await api.post(`/billing/eligibility/${clientId}`)).data); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Card className="p-6 max-w-2xl" data-testid="rc-eligibility">
      <h3 className="mb-1 font-heading text-base font-bold text-[#3E2723]">Eligibility Verification</h3>
      <p className="mb-4 text-xs text-[#7B6157]">Simulated 270/271 check against the client's insurance on file. Connect a clearinghouse for live payer verification.</p>
      <div className="flex items-end gap-3">
        <div className="flex-1"><FLabel>Client</FLabel><ClientSelect clients={clients} value={clientId} onChange={setClientId} testId="rc-elig-client" /></div>
        <Btn onClick={run} disabled={busy} data-testid="rc-elig-run-btn"><ShieldCheck size={14} /> {busy ? "Checking…" : "Verify Coverage"}</Btn>
      </div>
      {result && (
        <div className="mt-5" data-testid="rc-elig-result">
          <div className={`mb-3 rounded-xl px-4 py-3 text-sm font-bold ${result.status === "active" ? "bg-[#E8F5E9] text-[#2E7D32]" : "bg-[#FFF8E1] text-[#8D6E00]"}`}>
            {result.status === "active" ? "✓ Coverage ACTIVE - verified today" : "⚠ Needs information - coverage could not be confirmed"}
          </div>
          <div className="space-y-1.5">
            {result.checks.map((c) => (
              <div key={c.item} className="flex items-center gap-2 text-sm">
                {c.ok ? <CheckCircle2 size={14} className="text-[#2E7D32]" /> : <XCircle size={14} className="text-[#C62828]" />}
                <span className="font-medium text-[#3E2723]">{c.item}:</span><span className="text-[#7B6157]">{c.detail}</span>
              </div>
            ))}
          </div>
          {result.active_authorization && (
            <div className="mt-3 rounded-xl border border-[#EFEBE0] bg-[#FDFCF8] p-3 text-sm">
              <span className="font-semibold text-[#3E2723]">Active authorization:</span> {result.active_authorization.auth_number} - {result.active_authorization.sessions_used}/{result.active_authorization.sessions_approved} units used, expires {result.active_authorization.expires}
            </div>
          )}
          <p className="mt-3 text-[11px] text-[#84675B]">{result.note}</p>
        </div>
      )}
    </Card>
  );
}

export default function RevenueCycle() {
  const [tab, setTab] = useState("encounters");
  const [refreshKey, setRefreshKey] = useState(0);
  const [summary, setSummary] = useState(null);
  useEffect(() => {
    api.get("/billing/encounters").then((r) => {
      const rows = r.data;
      setSummary({
        unbilled: rows.filter((x) => x.status === "unbilled").reduce((a, x) => a + x.charge_amount, 0),
        billed: rows.filter((x) => x.status === "billed").reduce((a, x) => a + x.charge_amount, 0),
        paid: rows.filter((x) => x.status === "paid").reduce((a, x) => a + x.charge_amount, 0),
        count: rows.filter((x) => x.status !== "void").length,
      });
    }).catch(() => {});
  }, [tab, refreshKey]);
  const TABS = [["encounters", "Encounters", Receipt], ["claims", "Claims", FileOutput], ["denials", "Denials", AlertOctagon], ["fees", "Fee Schedule", DollarSign], ["eligibility", "Eligibility", ShieldCheck]];
  return (
    <div data-testid="revenue-cycle-page">
      <PageHeader title="Revenue Cycle" subtitle="Encounter capture → charge → authorization burn → claims (CMS-1500 / 837P) → payment tracking" />
      {summary && (
        <div className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
          {[["Unbilled charges", fmt(summary.unbilled), "text-[#E6A700]"], ["Billed (pending)", fmt(summary.billed), "text-[#1565C0]"],
            ["Collected", fmt(summary.paid), "text-[#2E7D32]"], ["Total encounters", summary.count, "text-[#4E342E]"]].map(([l, v, c]) => (
            <Card key={l} className="p-4" data-testid={`rc-stat-${l.toLowerCase().replace(/[^a-z]+/g, "-").replace(/-$/, "")}`}>
              <div className="text-[10px] font-bold uppercase tracking-wide text-[#84675B]">{l}</div>
              <div className={`mt-1 font-heading text-2xl font-bold ${c}`}>{v}</div>
            </Card>
          ))}
        </div>
      )}
      <div className="mb-4 flex flex-wrap gap-2">
        {TABS.map(([k, l, Icon]) => (
          <button key={k} data-testid={`rc-tab-${k}`} onClick={() => setTab(k)}
            className={`flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${tab === k ? "bg-[#4E342E] text-white" : "bg-[#F5F2EB] text-[#6D4C41] hover:bg-[#EDE7DC]"}`}>
            <Icon size={14} /> {l}
          </button>
        ))}
      </div>
      {tab === "encounters" && <EncountersTab onClaimCreated={() => setRefreshKey((k) => k + 1)} />}
      {tab === "claims" && <ClaimsTab refreshKey={refreshKey} />}
      {tab === "denials" && <DenialsTab />}
      {tab === "fees" && <FeeScheduleTab />}
      {tab === "eligibility" && <EligibilityTab />}
    </div>
  );
}
